<template>
  <div id="wrapper" @click="closeMobileMenu">

    <!-- chromeMode comes from route meta (see router/index.js), not from views
         emitting events. Previously every view emitted showWishboardCreate /
         showlogout / showWishboardList on mount, App.vue listened for them, and
         then the nav links ignored all of it and gated on isLoggedIn alone —
         so "Create a Wishboard" and "Sign out" appeared on every page,
         including Presentation. It also meant a new view had to remember to emit
         three flags correctly or its nav would be wrong.

         chrome: 'none'   -> no nav at all (Presentation)
         chrome: 'compact' -> logo only until hover/focus/tap (public board) -->
    <nav class="app-nav"
         :class="{
           'app-nav-compact': chromeMode === 'compact',
           'app-nav-minimal': chromeMode === 'minimal'
         }"
         v-show="chromeMode !== 'none'">
      <div class="nav-inner">
        <router-link to="/" class="nav-brand">
  <span class="wishboard-logo" style="font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; font-weight: 800;">Wishboard</span>
</router-link>

        <!-- Desktop links -->
        <div class="nav-links">
          <router-link v-if="isLoggedIn" to="/wishboardcreate" class="nav-link">Create a Wishboard</router-link>
          <router-link v-if="isLoggedIn" to="/wishboardlist" class="nav-link">My Boards</router-link>
          <router-link v-if="!isLoggedIn" to="/login" class="nav-link">Sign in</router-link>
          <router-link v-if="isLoggedIn" to="/logout" class="nav-link nav-link-muted">Sign out</router-link>
          <router-link v-if="showPayment === 'true'" to="/payment" class="nav-link">Payment</router-link>
        </div>

        <!-- Mobile burger -->
        <button class="nav-burger" @click.stop="showMobileMenu = !showMobileMenu" aria-label="menu">
          <i :class="showMobileMenu ? 'bx bx-x' : 'bx bx-menu'"></i>
        </button>
      </div>

      <!-- Mobile menu -->
      <div class="nav-mobile" v-if="showMobileMenu">
        <router-link v-if="isLoggedIn" to="/wishboardcreate" class="nav-mobile-link" @click="showMobileMenu = false">Create a Wishboard</router-link>
        <router-link v-if="isLoggedIn" to="/wishboardlist" class="nav-mobile-link" @click="showMobileMenu = false">My Boards</router-link>
        <router-link v-if="!isLoggedIn" to="/login" class="nav-mobile-link" @click="showMobileMenu = false">Sign in</router-link>
        <router-link v-if="isLoggedIn" to="/logout" class="nav-mobile-link" @click="showMobileMenu = false">Sign out</router-link>
      </div>
    </nav>

    <!-- Main content — no extra padding, pages own their spacing -->
    <main class="app-main" :class="{ 'app-main-fullbleed': chromeMode === 'none' }">
      <router-view/>
    </main>

    <footer class="app-footer">
      <span :title="buildStamp" @click="copyBuildStamp">
        Wishboard v{{ appVersion }}<span v-if="buildSha" class="footer-build"> · {{ buildSha }}</span>
      </span>
      <div class="footer-links">
        <router-link to="/Privacy">Privacy Policy</router-link>
        <router-link to="/TermsOfService">Terms of Service</router-link>
        <a href="mailto:care@thewishboard.com">Contact Us</a>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: 'App',
  data() {
    return {
      showMobileMenu: false,
      showLogin: 'false',
      showLogout: 'false',
      showWishboardCreate: 'false',
      showPayment: 'false',
      showNavbar: true,
    }
  },
  computed: {
    // 'none' | 'compact' | 'full' -- read from the matched route's meta.
    chromeMode() {
      const m = this.$route && this.$route.meta
      return (m && m.chrome) || 'full'
    },
    isLoggedIn() {
      return !!this.$store.getters.getGToken
    },
    // Injected at build time by DefinePlugin in vue.config.js.
    appVersion() {
      return typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0'
    },
    buildSha() {
      return typeof __BUILD_SHA__ !== 'undefined' ? __BUILD_SHA__ : ''
    },
    buildTime() {
      return typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : ''
    },
    buildStamp() {
      const parts = ['Wishboard v' + this.appVersion]
      if (this.buildSha) parts.push('build ' + this.buildSha)
      if (this.buildTime) parts.push(this.buildTime)
      return parts.join(' · ') + '\nClick to copy'
    },
  },
  methods: {
    closeMobileMenu(event) {
      if (!event.target.closest('.nav-burger') && !event.target.closest('.nav-mobile')) {
        this.showMobileMenu = false
      }
    },
    setShowLogout(v) { this.showLogout = v },
    setShowLogin(v) { this.showLogin = v },
    setShowWishboardCreate(v) { this.showWishboardCreate = v },
    setShowPaymente(v) { this.showPayment = v },
    copyBuildStamp() {
      const text = this.buildStamp.split('\n')[0];
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).catch(() => {});
      }
    },
  },
  beforeCreate() {
    this.$store.commit('initializeStore')
  },
  created() {
    this.emitter.on('showlogout', v => { this.showLogout = v })
    this.emitter.on('showWishboardCreate', v => { this.showWishboardCreate = v })
    this.emitter.on('showPayment', v => { this.showPayment = v })
  },
}
</script>

<style lang="scss">
@import '../node_modules/bulma';
</style>

<style>
  @import "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap";
  @import "https://unpkg.com/boxicons@2.0.9/css/boxicons.min.css";
  @import "./assets/styles/styles_from_django.css";

  /* ── GLOBAL RESET & FONT ── */
  *, *::before, *::after { box-sizing: border-box; }

  html, body {
    margin: 0;
    padding: 0;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 16px;
    color: #111;
    background: #fff;
    -webkit-font-smoothing: antialiased;
  }

  a { text-decoration: none; color: inherit; }

  /* ── NAVBAR ── */
  /* Compact mode (route meta chrome:'compact'): the nav shrinks to just the logo
   and reveals its links on hover, keyboard focus, or tap. Keeps every link one
   gesture away without letting them sit permanently over a shared keepsake.
   The nav bar itself stays fixed, so nothing shifts underneath it. */
/* Minimal (route meta chrome:'minimal'): the brand alone, so a presented board
   keeps a visible way back to the site without the account links sitting over it.
   Opaque rather than translucent -- it now sits directly over board content
   instead of over page background, so it needs to be readable on its own. */
.app-nav-minimal { background: rgba(255,255,255,0.94); }
.app-nav-minimal .nav-links,
.app-nav-minimal .nav-burger {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 0.18s ease, visibility 0.18s;
}
.app-nav-minimal:hover .nav-links,
.app-nav-minimal:focus-within .nav-links {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
}

.app-nav-compact .nav-links {
  opacity: 0;
  visibility: hidden;
  transform: translateX(8px);
  pointer-events: none;
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s;
}
.app-nav-compact .nav-brand::after {
  /* a subtle dot hints there is something there, without adding a control */
  content: '';
  display: inline-block;
  width: 4px;
  height: 4px;
  margin-left: 6px;
  border-radius: 50%;
  background: #e8a020;
  vertical-align: middle;
  opacity: 0.85;
}
.app-nav-compact:hover .nav-links,
.app-nav-compact:focus-within .nav-links {
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
  pointer-events: auto;
}
.app-nav-compact:focus-within .nav-brand::after { opacity: 0; }

/* Touch: no hover, so treat the burger as the way in. Always show it here. */
@media (hover: none) {
  .app-nav-compact .nav-links {
    opacity: 1;
    visibility: visible;
    transform: none;
    pointer-events: auto;
  }
  .app-nav-compact .nav-brand::after { opacity: 0; }
}

.app-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 100;
    background: rgba(255,255,255,0.92);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid #f0f0f0;
    /* Sit below the iPhone status bar on notched devices */
    padding-top: env(safe-area-inset-top, 0px);
  }

  .nav-inner {
    max-width: 100%;
    margin: 0;
    padding: 0 32px;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
  }

  .nav-brand {
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: -0.3px;
    color: #0a0a0a !important;
    text-decoration: none;
    flex-shrink: 0;
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .nav-link {
    font-size: 0.82rem;
    font-weight: 600;
    color: #555 !important;
    padding: 6px 12px;
    border-radius: 6px;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }

  .nav-link:hover {
    background: #f5f5f5;
    color: #111 !important;
  }

  .nav-link.router-link-active {
    color: #0a0a0a !important;
    background: #f0f0f0;
  }

  .nav-link-muted {
    color: #aaa !important;
  }

  .nav-link-muted:hover {
    color: #555 !important;
  }

  .nav-burger {
    display: none;
    background: none;
    border: none;
    font-size: 1.4rem;
    cursor: pointer;
    color: #333;
    padding: 4px;
  }

  .nav-mobile {
    border-top: 1px solid #f0f0f0;
    padding: 8px 16px 16px;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .nav-mobile-link {
    font-size: 0.9rem;
    font-weight: 600;
    color: #333;
    padding: 10px 12px;
    border-radius: 8px;
    display: block;
  }

  .nav-mobile-link:hover { background: #f5f5f5; }

  /* ── MAIN ── */
  .app-main {
    padding-top: calc(56px + env(safe-area-inset-top, 0px)); /* navbar height + notch */
    /* Grow to push the footer down, but never shrink below the content -- a
       40-wish board must overflow and scroll normally, not compress. */
    flex: 1 0 auto;

  }
  /* With the nav hidden (route meta chrome:'none') that reserved band would be a
     dead gap at the top of a full-bleed page, so give the space back. */
  .app-main-fullbleed {
    padding-top: 0;
  }

  /* ── FOOTER ── */
  .app-footer {
    border-top: 1px solid #f0f0f0;
    padding: 24px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 0.78rem;
    color: #bbb;
  }

  .footer-links {
    display: flex;
    gap: 16px;
  }

  /* Build stamp: monospace + click affordance so it reads as diagnostics,
     not marketing. Kept muted so it doesn't compete with the board content. */
  .app-footer > span:first-child {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    cursor: copy;
    user-select: all;
  }

  .footer-build {
    opacity: 0.75;
  }

  .footer-links a {
    color: #bbb;
    font-size: 0.78rem;
    transition: color 0.15s;
  }

  .footer-links a:hover { color: #555; }

  /* ── RESPONSIVE ── */
  @media (max-width: 768px) {
    .nav-links { display: none; }
    .nav-burger { display: flex; }
    .nav-inner { padding: 0 20px; }
  }
</style>
